import React from "react";
import { motion } from "framer-motion";

/* =========================================================
   CLIENT DATA
========================================================= */

const clients = [
    { name: "BookMyShow", logo: "/assets/logos/logo1.svg" },
    { name: "Ecolab", logo: "/assets/logos/logo2.svg" },
    { name: "J.P. Morgan", logo: "/assets/logos/logo3.svg" },
    { name: "Brandix", logo: "/assets/logos/logo4.svg" },
    { name: "ABB", logo: "/assets/logos/logo5.svg" },
    { name: "IKEA", logo: "/assets/logos/logo8.svg" },
    { name: "QWIC", logo: "/assets/logos/logo6.svg" },
    { name: "STL", logo: "/assets/logos/logo10.svg" },
    { name: "Tenerity", logo: "/assets/logos/logo9.svg" },
    { name: "Bahrain Duty Free", logo: "/assets/logos/logo7.svg" },
    { name: "K-Flex", logo: "/assets/logos/logo11.svg" },
    { name: "Honeywell", logo: "/assets/logos/logo12.svg" },
    { name: "Cargo Service Center", logo: "/assets/logos/logo13.svg" },
    { name: "HPX", logo: "/assets/logos/logo14.svg" },
    { name: "YK Electric", logo: "/assets/logos/logo15.svg" },
    { name: "Fazar", logo: "/assets/logos/logo16.svg" },
    { name: "Logo 17", logo: "/assets/logos/logo17.svg" },
    { name: "Logo 18", logo: "/assets/logos/logo18.svg" },
];


/* =========================================================
   LOGO CARD
========================================================= */

const LogoCard = ({ client, index }) => {
    return (
        <motion.div
            initial={{
                opacity: 0,
                y: 18,
            }}
            whileInView={{
                opacity: 1,
                y: 0,
            }}
            viewport={{
                once: true,
            }}
           
            transition={{
                duration: 0.35,
                ease: [0.22, 1, 0.36, 1],
            }}
            className="
                group
                relative
                flex-shrink-0
                w-[175px]
                sm:w-[195px]
                lg:w-[220px]
                h-[105px]
                lg:h-[125px]
                flex
                items-center
                justify-center
                overflow-hidden
                bg-white
                border
                border-[#01386A]/[0.08]
                shadow-[0_8px_30px_rgba(1,56,106,0.035)]
                transition-all
                duration-500
                hover:border-[#01386A]/[0.18]
                hover:shadow-[0_18px_50px_rgba(1,56,106,0.10)]
            "
        >
            {/* =================================================
                SOFT CENTER GLOW
            ================================================= */}

            <div
                className="
                    absolute
                    inset-0
                    bg-[radial-gradient(circle_at_center,rgba(237,104,0,0.08),transparent_65%)]
                    opacity-0
                    group-hover:opacity-100
                    transition-opacity
                    duration-500
                    pointer-events-none
                "
            />

            {/* =================================================
                TOP RIGHT CORNER
            ================================================= */}

            <span
                className="
                    absolute
                    top-0
                    right-0
                    w-[25px]
                    h-[25px]
                    border-t
                    border-r
                    border-[#ED6800]/0
                    group-hover:border-[#ED6800]/70
                    transition-all
                    duration-500
                "
            />

            {/* =================================================
                BOTTOM LEFT CORNER
            ================================================= */}

            <span
                className="
                    absolute
                    bottom-0
                    left-0
                    w-[25px]
                    h-[25px]
                    border-b
                    border-l
                    border-[#01386A]/0
                    group-hover:border-[#01386A]/40
                    transition-all
                    duration-500
                "
            />

            {/* =================================================
                SMALL TECH DOT
            ================================================= */}

            <span
                className="
                    absolute
                    top-[12px]
                    left-[12px]
                    w-[4px]
                    h-[4px]
                    rounded-full
                    bg-[#ED6800]/30
                    group-hover:bg-[#ED6800]
                    transition-colors
                    duration-300
                "
            />

            {/* =================================================
                LOGO
            ================================================= */}

            <motion.img
                src={client.logo}
                alt={`${client.name} logo`}
                whileHover={{
                    scale: 1.06,
                }}
                transition={{
                    duration: 0.35,
                    ease: "easeOut",
                }}
                className="
                    relative
                    z-10
                    max-w-[125px]
                    lg:max-w-[155px]
                    max-h-[55px]
                    lg:max-h-[68px]
                    w-auto
                    h-auto
                    object-contain
                    opacity-80
                    group-hover:opacity-100
                    transition-opacity
                    duration-300
                "
            />

            {/* =================================================
                BOTTOM ORANGE INDICATOR
            ================================================= */}

            <span
                className="
                    absolute
                    bottom-[10px]
                    left-1/2
                    -translate-x-1/2
                    w-0
                    h-[2px]
                    bg-[#ED6800]
                    group-hover:w-[32px]
                    transition-all
                    duration-400
                "
            />
        </motion.div>
    );
};


/* =========================================================
   MARQUEE ROW
========================================================= */

const MarqueeRow = ({
    items,
    direction = "left",
    duration = 35,
}) => {
    // Triple copy keeps the marquee seamless
    const repeatedItems = [...items, ...items, ...items];

    return (
        <div className="relative overflow-hidden">

            <motion.div
                className="
                    flex
                    gap-[12px]
                    sm:gap-[15px]
                    lg:gap-[18px]
                    w-max
                "
                animate={{
                    x:
                        direction === "left"
                            ? ["0%", "-33.333%"]
                            : ["-33.333%", "0%"],
                }}
                transition={{
                    duration,
                    repeat: Infinity,
                    ease: "linear",
                }}
            >
                {repeatedItems.map((client, index) => (
                    <LogoCard
                        key={`${client.name}-${index}`}
                        client={client}
                        index={index}
                    />
                ))}
            </motion.div>
        </div>
    );
};


/* =========================================================
   CLIENTS SECTION
========================================================= */

const ClientsSection = () => {

    const row1 = clients.slice(0, 7);
    const row2 = clients.slice(7, 13);
    const row3 = clients.slice(13, 18);

    return (
        <section
            className="
                relative
                w-full
                bg-white
                py-[65px]
                lg:py-[105px]
                overflow-hidden
                font-sans
            "
        >

            {/* =================================================
                BACKGROUND GRID
            ================================================= */}

            <div
                className="
                    absolute
                    inset-0
                    pointer-events-none
                    opacity-[0.035]
                "
                style={{
                    backgroundImage: `
                        linear-gradient(#01386A 1px, transparent 1px),
                        linear-gradient(90deg, #01386A 1px, transparent 1px)
                    `,
                    backgroundSize: "70px 70px",
                }}
            />


            {/* =================================================
                TOP BLUE GLOW
            ================================================= */}

            <div
                className="
                    absolute
                    top-[-150px]
                    left-[-150px]
                    w-[550px]
                    h-[550px]
                    rounded-full
                    bg-[#01386A]/[0.035]
                    blur-[110px]
                    pointer-events-none
                "
            />


            {/* =================================================
                BOTTOM ORANGE GLOW
            ================================================= */}

            <div
                className="
                    absolute
                    bottom-[-180px]
                    right-[-120px]
                    w-[500px]
                    h-[500px]
                    rounded-full
                    bg-[#ED6800]/[0.035]
                    blur-[110px]
                    pointer-events-none
                "
            />


            {/* =================================================
                CENTER GLOW
            ================================================= */}

            <div
                className="
                    absolute
                    left-1/2
                    top-1/2
                    -translate-x-1/2
                    -translate-y-1/2
                    w-[700px]
                    h-[320px]
                    rounded-full
                    bg-[#01386A]/[0.025]
                    blur-[100px]
                    pointer-events-none
                "
            />


            {/* =================================================
                MAIN CONTAINER
            ================================================= */}

            <div
                className="
                    max-w-[1440px]
                    w-full
                    mx-auto
                    px-[20px]
                    lg:px-[80px]
                    relative
                    z-10
                "
            >

                {/* =================================================
                    HEADER
                ================================================= */}

                <div
                    className="
                        flex
                        flex-col
                        lg:flex-row
                        lg:items-end
                        lg:justify-between
                        gap-7
                        mb-[45px]
                        lg:mb-[65px]
                    "
                >

                    {/* LEFT */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 25,
                        }}
                        whileInView={{
                            opacity: 1,
                            y: 0,
                        }}
                        viewport={{
                            once: true,
                        }}
                        transition={{
                            duration: 0.7,
                        }}
                    >

                        {/* Eyebrow */}

                        <div
                            className="
                                flex
                                items-center
                                gap-3
                                mb-4
                            "
                        >

                          
                            <span
                                className="
                                    text-[#01386A]
                                    font-sora 
                                    text-[11px]
                                    lg:text-[12px]
                                    font-[600]
                                    tracking-[0.18em]
                                    uppercase
                                "
                            >
                                Our Clients
                            </span>

                        </div>


                        {/* Heading */}

                        <h2
                           className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] lg:mb-[60px] mb-[30px] relative z-10 leading-[1.2]"
                        >
                            Trusted by businesses  across industries.
                            <br />

                           

                        </h2>

                    </motion.div>


                    {/* RIGHT DESCRIPTION */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 20,
                        }}
                        whileInView={{
                            opacity: 1,
                            y: 0,
                        }}
                        viewport={{
                            once: true,
                        }}
                        transition={{
                            duration: 0.7,
                            delay: 0.15,
                        }}
                        className="
                            max-w-[500px]
                            lg:pb-[3px]
                        "
                    >

                        <p
                            className="
                                text-[#01386A]/60
                                font-graphik
                                text-[14px]
                                lg:text-[15px]
                                font-[500]
                                leading-[1.75]
                            "
                        >
                            From growing businesses to global enterprises,
                            we build long-term technology partnerships that
                            turn complex challenges into meaningful results.
                        </p>

                    </motion.div>

                </div>


                {/* =================================================
                    CLIENT LOGO WALL
                ================================================= */}

                <motion.div
                    initial={{
                        opacity: 0,
                        y: 25,
                    }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                    }}
                    transition={{
                        duration: 0.8,
                        delay: 0.15,
                    }}
                    className="relative"
                >

                    {/* =================================================
                        LEFT FADE
                    ================================================= */}

                    <div
                        className="
                            absolute
                            left-0
                            top-0
                            bottom-0
                            w-[60px]
                            sm:w-[90px]
                            lg:w-[170px]
                            bg-gradient-to-r
                            from-white
                            via-white/90
                            to-transparent
                            z-20
                            pointer-events-none
                        "
                    />


                    {/* =================================================
                        RIGHT FADE
                    ================================================= */}

                    <div
                        className="
                            absolute
                            right-0
                            top-0
                            bottom-0
                            w-[60px]
                            sm:w-[90px]
                            lg:w-[170px]
                            bg-gradient-to-l
                            from-white
                            via-white/90
                            to-transparent
                            z-20
                            pointer-events-none
                        "
                    />


                    {/* =================================================
                        ROW 1
                    ================================================= */}

                    <MarqueeRow
                        items={row1}
                        direction="left"
                        duration={32}
                    />


                    {/* =================================================
                        ROW 2
                    ================================================= */}

                    <div className="mt-[12px] lg:mt-[18px]">

                        <MarqueeRow
                            items={row2}
                            direction="right"
                            duration={38}
                        />

                    </div>


                    {/* =================================================
                        ROW 3
                    ================================================= */}

                    <div className="mt-[12px] lg:mt-[18px]">

                        <MarqueeRow
                            items={row3}
                            direction="left"
                            duration={35}
                        />

                    </div>

                </motion.div>


                {/* =================================================
                    BOTTOM INFORMATION BAR
                ================================================= */}

                <motion.div
                    initial={{
                        opacity: 0,
                        y: 15,
                    }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                    }}
                    transition={{
                        duration: 0.7,
                        delay: 0.35,
                    }}
                    className="
                        mt-[45px]
                        lg:mt-[60px]
                        pt-[22px]
                        border-t
                        border-[#01386A]/10
                        flex
                        flex-col
                        sm:flex-row
                        sm:items-center
                        sm:justify-between
                        gap-4
                    "
                >

                    {/* LEFT */}

                    <div
                        className="
                            flex
                            items-center
                            gap-3
                        "
                    >

                        <span
                            className="
                                relative
                                flex
                                items-center
                                justify-center
                                w-[9px]
                                h-[9px]
                            "
                        >
                            <span
                                className="
                                    absolute
                                    w-full
                                    h-full
                                    rounded-full
                                    bg-[#ED6800]/20
                                    animate-ping
                                "
                            />

                            <span
                                className="
                                    relative
                                    w-[6px]
                                    h-[6px]
                                    rounded-full
                                    bg-[#ED6800]
                                "
                            />
                        </span>


                        <span
                            className="
                                text-[#01386A]/50
                                font-graphik
                                text-[10px]
                                lg:text-[11px]
                                uppercase
                                tracking-[0.14em]
                            "
                        >
                            Technology partnerships that last
                        </span>

                    </div>


                    {/* RIGHT */}

                    <div
                        className="
                            flex
                            items-center
                            gap-3
                        "
                    >

                        <span
                            className="
                                font-sora
                                text-[22px]
                                lg:text-[26px]
                                font-[600]
                                text-[#01386A]
                            "
                        >
                            18+
                        </span>

                        <span
                            className="
                                text-[#01386A]/35
                                font-graphik
                                text-[10px]
                                lg:text-[11px]
                                uppercase
                                tracking-[0.12em]
                            "
                        >
                            Brands & growing
                        </span>

                    </div>

                </motion.div>

            </div>

        </section>
    );
};

export default ClientsSection;